// ELD Tracker Domain Configuration
export const eldtrackerConfig = {
  // Basic domain information
  id: 'eldtracker',
  name: 'ELD Tracker',
  tagline: 'ELD Tracking and Compliance Solutions by Geotab',
  description: 'Comprehensive hours of service tracking and compliance management.',
  
  // Assets
  logo: '/assets/logos/eldtracker-logo',
  whiteLogo: '/assets/logos/eldtracker-white-logo',
  favicon: '/assets/favicons/eldtracker-favicon.png',
  icon: '/assets/icons/eldtracker',
  
  // Navigation
  pages: ['home', 'solutions', 'blog'],
  navigation: [
    { name: 'Home', path: '/' },
    { name: 'Solutions', path: '/solutions' },
    { name: 'Blog', path: '/blog' },
  ],
  
  // CTAs
  cta: {
    primary: 'Request Demo',
    secondary: 'Buy Now'
  },

  // Shop link
  shopLink: 'https://shop.fleethoster.com/collections/geotab',
  
  // UI Configuration
  variant: 'corporate',
  showFeatures: true,
  showTestimonials: true,
  
  // Content sections
  hero: {
    title: 'All-in-one ELD Tracker platform',
    subtitle: 'Streamline ELD compliance while optimizing driver productivity and safety.',
    features: ['HOS reporting', 'Log management', 'Vehicle inspections', 'Simplified roadside inspections']
  },
  
  // Gallery/Image Slider configuration
  gallery: {
    title: 'ELD Tracker Solutions',
    subtitle: 'Explore our comprehensive ELD tracking and compliance solutions',
    description: 'See how ELD Tracker transforms fleet compliance with advanced tracking technology'
  },

  features: {
    title: 'ELD Tracker Key features by Geotab',
    subtitle: 'Best practices for fleet success.',
    description: 'ELD Tracker is a comprehensive ELD tracking and compliance solutions.',
    items: [
      {
        title: 'Cross border compliance',
        description: '100+ HOS rulesets & exemptions for the U.S. & Canada. Intrastate, short-haul, agriculture, etc.',
        icon: '🌍'
      },
      {
        title: 'Driver ID',
        description: 'Auto-log which drivers use which vehicles. Track driver behaviors & identify unauthorized use.',
        icon: '👤'
      },
      {
        title: 'Offline support for out of cellular coverage use',
        description: 'Work offline and auto-sync data when coverage is restored. Connect our IOX-USB cable for offline ELD.',
        icon: '📡'
      },
      {
        title: 'Simplified roadside inspections',
        description: 'Complete roadside inspections via app. Submit instantly. Includes PIN protection.',
        icon: '🚗'
      }
    ]
  },
  
  solutions: {
    title: 'Integrate and customize your ELD solution',
    subtitle: 'Get more value from your ELD with Geotab’s open APIs and Software Development Kit.',
    description: 'Stay compliant with regulations while optimizing driver productivity and safety.',
    sections: [
      {
        title: 'HOS monitoring',
        description: "Track drivers' HOS status in real time. View driver status, driving time, remaining hours, and required breaks. All in on one dashboard. Get alerts when drivers are approaching or exceeding HOS limits. Streamline ELD compliance.",
      },
      {
        title: 'Cross border',
        description: "Ensure cross-border compliance between the U.S. and Canada. Auto-switch between 100+ HOS rulesets as drivers cross borders. Intrastate, short-haul, 16-hour workdays, and more. Get real-time alerts for cross-border movements.",
      },
      {
        title: 'Log management',
        description: 'Auto-update logs based on vehicle movements, border crossings, and more. Edit and annotate logs via mobile app. Send drivers reminders to certify logs and claim unassigned driving.',
      },
      {
        title: 'Vehicle inspections',
        description: 'Enable DVIR completion via mobile app. Pre- and post-trip. Create custom inspection checklists based on vehicle types and regulatory requirements. Get alerts for completed inspections and vehicle issues.',
      },
      {
        title: 'Reporting',
        description: 'Auto-generate ELD-compliant reports for driver hours, rest periods, work limits, and more. Keep detailed audit trails, including driver activity and sleeper berth status. Get all your data at your fingertips and streamline audits.',
      },
    ]
  },
  
  
  testimonials: [
    {
      content: "The platform is easy to use and very driver friendly.",
      author: "Jennifer Y.",
      role: "DOT Compliance Manager II",
      company: "",
      avatar: "https://randomuser.me/api/portraits/women/48.jpg"
    },
    {
      content: "We will continue to use the scorecard and other telematics provided by Geotab to change the culture of safety within our company.",
      author: "Daryn Rabb",
      role: "Health and Safety Manager",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/12.jpg"
    },
    {
      content: "Our company has been with 2 other ELD platforms, but Geotab has been by far the best for the money.",
      author: "Jeff M.",
      role: "VP, Safety",
      company: "",
      avatar: "https://randomuser.me/api/portraits/men/49.jpg"
    }
  ],

  blog: {
    title: 'ELD Tracker Blog',
    subtitle: '',
    description: 'Keep up to date with the latest news and insights in fleet management and industry best practices.',
  },
  
  // SEO and Meta
  seo: {
    // Basic Meta Tags
    title: 'ELD Tracker - HOS Management & DOT Compliance Solutions',
    description: 'Track hours of service and manage compliance effortlessly. Stay DOT compliant and optimize driver productivity with advanced HOS tools.',
    keywords: [
      'hours of service', 'fleet compliance', 'driver management', 'DOT compliance', 'fleet tracking',
      'HOS management', 'driver hours tracking', 'fleet compliance software', 'DOT regulations', 'driver productivity',
      'fleet safety compliance', 'hours of service software', 'driver management system', 'fleet regulatory compliance',
      'HOS tracking', 'fleet compliance management', 'driver hours management', 'fleet safety regulations'
    ],
    
    // Open Graph & Social Media
    ogTitle: 'ELD Tracker - Professional HOS Management Solutions',
    ogDescription: 'Comprehensive hours of service tracking and compliance management. Stay compliant, optimize productivity.',
    ogType: 'website',
    ogUrl: 'https://www.eldtracker.com',
    
    // Twitter Cards
    twitterCard: 'summary_large_image',
    twitterTitle: 'ELD Tracker - Professional HOS Management Solutions',
    twitterDescription: 'Comprehensive hours of service tracking and compliance management. Stay compliant, optimize productivity.',
    
    // Robots
    robots: 'index, follow',
    author: 'ELD Tracker',
    publisher: 'ELD Tracker',
    
    // Page-Specific SEO
    pages: {
      home: {
        title: 'ELD Tracker - HOS Management & DOT Compliance Solutions',
        description: 'Track hours of service and manage compliance effortlessly. Stay DOT compliant and optimize driver productivity with advanced HOS tools.',
        keywords: ['hours of service', 'fleet compliance', 'driver management', 'DOT compliance', 'fleet tracking']
      },
      solutions: {
        title: 'ELD Tracker Solutions - HOS Management Services',
        description: 'Comprehensive HOS management solutions and compliance services. Professional driver management and regulatory compliance tools.',
        keywords: ['HOS management solutions', 'fleet compliance services', 'driver management', 'regulatory compliance']
      },
      products: {
        title: 'ELD Tracker Products - HOS Management Software & Tools',
        description: 'Professional HOS management software and compliance tools. Driver tracking, hours management, and regulatory compliance solutions.',
        keywords: ['HOS management software', 'compliance tools', 'driver tracking software', 'hours management']
      },
      blog: {
        title: 'ELD Tracker News & Insights - HOS Compliance Blog',
        description: 'Latest news, insights, and best practices for hours of service management and fleet compliance. Professional tips and regulatory updates.',
        keywords: ['HOS management news', 'fleet compliance insights', 'driver management tips', 'regulatory compliance best practices']
      }
    },
    
    // Performance & Technical SEO
    technical: {
      pageLoadTime: '< 3 seconds',
      mobileOptimized: true,
      sslEnabled: true,
      schemaMarkup: true,
      xmlSitemap: true,
      robotsTxt: true,
      compressionEnabled: true,
      imageOptimization: true,
      cdnEnabled: true
    },

    // Local Business Info for Structured Data
    localBusiness: {
      streetAddress: '',
      addressLocality: 'Atlanta',
      addressRegion: 'GA',
      postalCode: '30308',
      addressCountry: 'US'
    }
  }
};
